import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  useCreateKaizenResultValidation,
  useEligibleKaizenSnapshots,
  useKaizenResultValidations,
} from '../../hooks/useKaizenResultValidations';
import GainsMonitorService from '../../services/gainsMonitorService';
import type { KaizenResultValidation } from '../../services/kaizenResultValidationService';
import {
  RESULT_METRIC_KEYS,
  RESULT_UI_LABEL,
  buildEvidenceContext,
  eligibleSnapshots,
  interpretEvidence,
  metricLabel,
  resultUiState,
  samePersonMeasuredAndValidated,
  type InterpretedMetric,
  type ResultDecision,
  type SnapshotResultSource,
} from '../../utils/kaizenResultValidation';

function formatDay(value: string | Date | null | undefined): string {
  if (value == null) return '—';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR');
}

/**
 * Dívida técnica: snapshots antigos foram gravados quando cycle_time,
 * ftr e capacity_fte tinham default 0, 100 e 0. Esses defaults já saíram
 * do banco. Zero gravado continua aparecendo como 0. Esta tela não
 * converte zero histórico em ausência e não altera evidence_context.
 */
function formatMetricValue(metric: InterpretedMetric['key'], value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  if (metric === 'lead_time' || metric === 'cycle_time') return GainsMonitorService.formatTime(value);
  if (metric === 'ftr') return `${value.toLocaleString('pt-BR')}%`;
  return `${value.toLocaleString('pt-BR')} FTE`;
}

function variationLabel(metric: InterpretedMetric): string {
  if (metric.assessment === 'neutral') return 'Igual';
  const down = metric.direction === 'lower_is_better'
    ? metric.assessment === 'positive'
    : metric.assessment === 'negative';
  const arrow = down ? '↓' : '↑';
  if (metric.improvementPercent == null) return metric.assessment === 'positive' ? `${arrow} melhor` : `${arrow} pior`;
  const percent = Math.abs(metric.improvementPercent).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return `${arrow} ${percent}%`;
}

function TargetSituation({ metric }: { metric: InterpretedMetric | null }) {
  if (!metric || metric.targetAssessment === 'unavailable') {
    return <span className="text-[var(--text-muted)]">—</span>;
  }
  const met = metric.targetAssessment === 'met';
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${
      met
        ? 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'
        : 'bg-amber-500/10 text-amber-800 dark:text-amber-200'
    }`}>
      {met ? 'Atingida' : 'Não atingida'}
    </span>
  );
}

function MetricTable({ metrics }: { metrics: InterpretedMetric[] }) {
  if (metrics.length === 0) {
    return <p className="text-xs text-[var(--text-muted)]">O contexto desta tentativa não pôde ser lido.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-left text-[11px]">
        <thead>
          <tr className="text-[var(--text-muted)]">
            <th className="py-1 pr-3 font-medium">Métrica</th>
            <th className="py-1 pr-3 font-medium">Baseline</th>
            <th className="py-1 pr-3 font-medium">Meta</th>
            <th className="py-1 pr-3 font-medium">Medido</th>
            <th className="py-1 pr-3 font-medium">Variação</th>
            <th className="whitespace-nowrap py-1 font-medium">Situação da meta</th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((metric) => (
            <tr key={metric.key} className="border-t border-[var(--border-default)] text-[var(--text-primary)]">
              <td className="py-1 pr-3">{metricLabel(metric.key)}</td>
              <td className="py-1 pr-3">{formatMetricValue(metric.key, metric.baseline)}</td>
              <td className="py-1 pr-3">{formatMetricValue(metric.key, metric.target)}</td>
              <td className="py-1 pr-3">{formatMetricValue(metric.key, metric.measured)}</td>
              <td className="whitespace-nowrap py-1 pr-3">{variationLabel(metric)}</td>
              <td className="whitespace-nowrap py-1"><TargetSituation metric={metric} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HistoryItem({
  attempt,
  snapshots,
}: {
  attempt: KaizenResultValidation;
  snapshots: SnapshotResultSource[];
}) {
  const [open, setOpen] = useState(false);
  const snapshot = snapshots.find((item) => item.id === attempt.snapshot_id);
  const metrics = attempt.evidence_context ? interpretEvidence(attempt.evidence_context) : [];
  const measuredAt = attempt.evidence_context?.measured_at;
  const samePerson = samePersonMeasuredAndValidated(snapshot?.created_by, attempt.created_by);
  const validated = attempt.decision === 'validated';
  return (
    <article className="border-t border-[var(--border-default)] py-2 text-[11px]">
      <div className="font-medium text-[var(--text-primary)]">{formatDay(attempt.created_at)}</div>
      <div className="text-[var(--text-secondary)]">
        {validated ? '✓ Validado' : 'Rejeitado'} · {attempt.validator_name || 'Usuário'}
      </div>
      <div className="text-[var(--text-muted)]">Medição: {formatDay(measuredAt)}</div>
      <button type="button" onClick={() => setOpen((current) => !current)} className="mt-1 text-[11px] font-medium text-[var(--brand-primary)]">
        {open ? 'Ocultar evidências' : 'Ver evidências'}
      </button>
      {open && (
        <div className="mt-2">
          <MetricTable metrics={metrics} />
          <p className="mt-2 text-[var(--text-secondary)]">Nota: {attempt.note?.trim() || '—'}</p>
          {samePerson && (
            <p className="text-[10px] text-[var(--text-muted)]">Medição e validação registradas pela mesma pessoa.</p>
          )}
        </div>
      )}
    </article>
  );
}

export const KaizenResultSection: React.FC<{ kaizenId: string }> = ({ kaizenId }) => {
  const { user } = useAuth();
  const review = useEligibleKaizenSnapshots(kaizenId);
  const validations = useKaizenResultValidations(kaizenId);
  const createValidation = useCreateKaizenResultValidation(kaizenId);
  const [open, setOpen] = useState(false);
  const [snapshotId, setSnapshotId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const kaizen = review.data?.kaizen;
  const snapshots = review.data?.snapshots ?? [];
  const eligible = useMemo(
    () => (kaizen ? eligibleSnapshots(kaizen, snapshots) : []),
    [kaizen, snapshots],
  );
  const selected = eligible.find((item) => item.id === snapshotId) ?? eligible[0] ?? null;
  const latestValidation = validations.data?.[0] ?? null;
  const gate = kaizen
    ? resultUiState({ kaizen, snapshots, latestValidation })
    : null;
  const preview = kaizen && selected ? buildEvidenceContext(kaizen, selected) : null;
  const previewMetrics = preview ? interpretEvidence(preview) : [];
  const selectedSamePerson = samePersonMeasuredAndValidated(selected?.created_by, user?.id);

  useEffect(() => {
    if (!selected) return;
    setSnapshotId((current) => current && eligible.some((item) => item.id === current) ? current : selected.id);
  }, [eligible, selected]);

  const close = () => {
    setOpen(false);
    setNote('');
    setFormError(null);
  };

  const submit = async (decision: ResultDecision) => {
    if (!selected) return;
    if (decision === 'rejected' && note.trim() === '') {
      setFormError('Informe a observação para rejeitar.');
      return;
    }
    setFormError(null);
    try {
      await createValidation.mutateAsync({
        snapshotId: selected.id,
        decision,
        note,
      });
      close();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Não foi possível registrar a validação.');
    }
  };

  const measuredFields: Record<(typeof RESULT_METRIC_KEYS)[number], number | null> = {
    lead_time: selected?.lead_time ?? null,
    cycle_time: selected?.cycle_time ?? null,
    ftr: selected?.ftr ?? null,
    capacity_fte: selected?.capacity_fte ?? null,
  };
  const baselineFields: Record<(typeof RESULT_METRIC_KEYS)[number], number | null> = {
    lead_time: kaizen?.baseline_lead_time ?? null,
    cycle_time: kaizen?.baseline_cycle_time ?? null,
    ftr: kaizen?.baseline_ftr ?? null,
    capacity_fte: kaizen?.baseline_capacity_fte ?? null,
  };
  const targetFields: Record<(typeof RESULT_METRIC_KEYS)[number], number | null> = {
    lead_time: kaizen?.target_lead_time ?? null,
    cycle_time: kaizen?.target_cycle_time ?? null,
    ftr: kaizen?.target_ftr ?? null,
    capacity_fte: kaizen?.target_capacity_fte ?? null,
  };
  const interpreted = new Map(previewMetrics.map((metric) => [metric.key, metric]));
  const hasHistory = (validations.data?.length ?? 0) > 0;

  return (
    <section className="rounded border border-[var(--border-default)] bg-[var(--bg-surface)] p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[13px] font-semibold text-[var(--text-primary)]">Resultado</h3>
        {gate && (
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
            gate.state === 'validated'
              ? 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'
              : gate.state === 'awaiting' || gate.state === 'rejected'
                ? 'bg-amber-500/10 text-amber-800 dark:text-amber-200'
                : 'bg-[var(--bg-surface-2)] text-[var(--text-muted)]'
          }`}>
            {RESULT_UI_LABEL[gate.state]}
          </span>
        )}
      </div>

      {review.isLoading && <p className="text-xs text-[var(--text-muted)]">Carregando resultado...</p>}
      {review.isError && (
        <p className="text-xs text-[var(--text-secondary)]">Não foi possível carregar o resultado medido.</p>
      )}

      {gate && kaizen && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-[11px]">
              <thead>
                <tr className="text-[var(--text-muted)]">
                  <th className="py-1 pr-3 font-medium">Métrica</th>
                  <th className="py-1 pr-3 font-medium">Baseline</th>
                  <th className="py-1 pr-3 font-medium">Meta</th>
                  <th className="py-1 pr-3 font-medium">Medido</th>
                  <th className="py-1 pr-3 font-medium">Variação</th>
                  <th className="whitespace-nowrap py-1 font-medium">Situação da meta</th>
                </tr>
              </thead>
              <tbody>
                {RESULT_METRIC_KEYS.map((key) => {
                  const metric = interpreted.get(key);
                  return (
                    <tr key={key} className="border-t border-[var(--border-default)] text-[var(--text-primary)]">
                      <td className="whitespace-nowrap py-1 pr-3">{metricLabel(key)}</td>
                      <td className="whitespace-nowrap py-1 pr-3">{formatMetricValue(key, baselineFields[key])}</td>
                      <td className="whitespace-nowrap py-1 pr-3">{formatMetricValue(key, targetFields[key])}</td>
                      <td className="whitespace-nowrap py-1 pr-3">{formatMetricValue(key, measuredFields[key])}</td>
                      <td className="whitespace-nowrap py-1 pr-3">{metric ? variationLabel(metric) : '—'}</td>
                      <td className="whitespace-nowrap py-1"><TargetSituation metric={metric ?? null} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {gate.message && <p className="mt-2 text-[11px] text-[var(--text-secondary)]">{gate.message}</p>}
          {selected && (
            <p className="mt-2 text-[11px] text-[var(--text-muted)]">
              {previewMetrics.length} {previewMetrics.length === 1 ? 'métrica' : 'métricas'} • medição de {formatDay(selected.measured_at)}
            </p>
          )}
          {hasHistory && latestValidation ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-[11px] text-[var(--text-secondary)]">
                {latestValidation.decision === 'validated' ? '✓ Resultado validado em' : 'Validação rejeitada em'}
                {' '}{formatDay(latestValidation.created_at)} por {latestValidation.validator_name || 'Usuário'}
              </p>
              <button
                type="button"
                disabled={!gate.canValidate}
                onClick={() => setOpen(true)}
                className="rounded border border-[var(--border-default)] px-2.5 py-1 text-[11px] font-medium text-[var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Nova validação
              </button>
            </div>
          ) : (
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                disabled={!gate.canValidate}
                onClick={() => setOpen(true)}
                className="rounded bg-[var(--brand-primary)] px-2.5 py-1 text-[11px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Validar resultado
              </button>
            </div>
          )}
        </>
      )}

      {validations.isError && (
        <p className="mt-2 text-xs text-gray-600 dark:text-slate-300">
          {validations.error instanceof Error && validations.error.name === 'KaizenResultValidationUnavailable'
            ? 'A validação de resultado ainda não está disponível neste ambiente.'
            : 'Não foi possível carregar as validações.'}
        </p>
      )}

      {(validations.data?.length ?? 0) > 0 && (
        <div className="mt-3">
          <h4 className="text-[11px] font-semibold text-[var(--text-primary)]">Histórico de validações</h4>
          {validations.data?.map((attempt) => (
            <HistoryItem key={attempt.id} attempt={attempt} snapshots={snapshots} />
          ))}
        </div>
      )}

      {open && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded bg-white p-4 shadow-lg dark:bg-slate-900">
            <h4 className="text-sm font-bold text-gray-900 dark:text-slate-100">Validar resultado</h4>
            <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
              Medição utilizada: {formatDay(selected.measured_at)}
            </p>
            {eligible.length > 1 && (
              <label className="mt-3 block text-xs text-gray-600 dark:text-slate-300">
                Escolher medição
                <select
                  value={selected.id}
                  onChange={(event) => setSnapshotId(event.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800"
                >
                  {eligible.map((item) => (
                    <option key={item.id} value={item.id}>{formatDay(item.measured_at)}</option>
                  ))}
                </select>
              </label>
            )}
            <div className="mt-3">
              <MetricTable metrics={previewMetrics} />
            </div>
            <p className="mt-3 text-xs text-gray-600 dark:text-slate-300">
              Validador: {user?.name || 'Usuário atual'}
            </p>
            {selectedSamePerson && (
              <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                Medição e validação registradas pela mesma pessoa.
              </p>
            )}
            <label className="mt-3 block text-xs text-gray-600 dark:text-slate-300">
              Observação
              <textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                rows={3}
                className="mt-1 w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800"
              />
            </label>
            {formError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{formError}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={close} className="rounded px-3 py-1.5 text-xs text-gray-600 dark:text-slate-300">
                Cancelar
              </button>
              <button
                type="button"
                disabled={createValidation.isPending}
                onClick={() => submit('rejected')}
                className="rounded border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-800 dark:border-slate-600 dark:text-slate-100"
              >
                Rejeitar validação
              </button>
              <button
                type="button"
                disabled={createValidation.isPending}
                onClick={() => submit('validated')}
                className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white"
              >
                Confirmar resultado
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
